import { filter, map, Observable } from "rxjs"

/**
 * A Channel is an abstract service that provides
 * bi-direction communication between two agents.
 * 
 * Each agent has a "Port", an instance of a service
 * that can send messages of Send type, and a "Receive"
 * observable that collects messages from the other agent.
 */
export namespace Channel {
  /**
   * Channels _may_ have a client-server relationship. In this case,
   * each agent has a well defined role, and we know what messages
   * that agent would send (and thus receive).
   */
  export type Type<ClientMessage = unknown, ServerMessage = unknown> = {
    client: ClientMessage,
    server: ServerMessage
  };

  export namespace Port {
    export type Client<CT extends Type> = Port<CT["client"], CT["server"]>;
    export type Server<CT extends Type> = Port<CT["server"], CT["client"]>;

    export type Uniform<T> = Port<T, T>;
  }
  export namespace Message {
    export type Client<CT extends Type> = CT["client"];
    export type Server<CT extends Type> = CT["server"];
  }

  /**
   * A "Channel Port" is an interface that represent one agent connected into a
   * channel, with the ability to hear messages broadcast and submit their own.
   * 
   * A channel port does not automatically include meta-controls, like closing
   * or listening to closing events.
   */
  export type Port<Send = unknown, Receive = unknown> = {
    send(message: Send): void,
    receive: Observable<Receive>,
  };
}

export const Channel = {
  /**
   * Convert one channel into another,
   * mapping and filtering its messages.
   * 
   * @param port 
   * @returns 
   */
  map: <Send, Receive>(
    port: Channel.Port<Send, Receive>,
  ) => <Send2, Receive2>(mapFuncs: {
    send: (send: Send2) => Send | null,
    receive: (receive: Receive) => Receive2 | null
  }): Channel.Port<Send2, Receive2> => {
    return {
      send(message) {
        const message2 = mapFuncs.send(message)
        if (message2)
          port.send(message2);
      },
      receive: port.receive
        .pipe(map(receive => mapFuncs.receive(receive)))
        .pipe(filter((x): x is Receive2 => !!x))
    }
  }
}